/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { gql } from '@apollo/client';
import { assembleQuery, State } from '@exo/frontend-common-apollo';
import { useQueryWithContext } from '@exo/frontend-common-hooks';
import { QueryInitiateExternalPaymentArgs } from '@exo/frontend-gql-generated-types/graphql';

export const initiateExternalPaymentFragment = gql`
  fragment InitiateExternalPaymentFields on PayExInitiatePaymentResponse {
    id
    wasSuccessful
    bankAccounts {
      id
      accountNumber
      description
    }
    differenceReasons {
      id
      category
      code
      description
    }
    docDetails {
      id
      amount {
        value
        currency
        formattedValue
        priceType
      }
      amountPriceType
      docNumber
      docDate
      paidAmount {
        value
        currency
        formattedValue
        priceType
      }
      paidAmountPriceType
      payInFull
      reference
      multipleReasons {
        id
        comments
        deductedAmount {
          value
          currency
          formattedValue
          priceType
        }
        deductedAmountPriceType
        reason {
          id
          category
          code
          description
        }
      }
    }
    fromRange
    toRange
    paymentDetails {
      id
      bankData {
        id
        accountNumber
        description
      }
      comments
      currency
      depositDate
      depositNo
      paidAmountPriceType
    }
    ruleWarnings {
      id
      code
      message
      subject
    }
    ruleErrors {
      id
      code
      message
      subject
    }
  }
`;

export const initiateExternalPaymentQuery = assembleQuery(
  fragmentNames => `
    query InitiateExternalPayment(
      $baseSiteId: ID!
      $baseStoreId: ID!
      $b2bUnitId: ID!
      $docNumbers: [String!]!
    ) {
      initiateExternalPayment(
        baseSiteId: $baseSiteId
        baseStoreId: $baseStoreId
        b2bUnitId: $b2bUnitId
        docNumbers: $docNumbers
      ) {
        ${fragmentNames.join('\n')}
      }
    }
  `,
  initiateExternalPaymentFragment
);

export type InitiateExternalPaymentArgsType = Omit<
  QueryInitiateExternalPaymentArgs,
  'baseSiteId' | 'baseStoreId' | 'b2bUnitId'
>;

export const useInitiateExternalPaymentQuery = <T>(
  docNumbers: string[]
): Result<T> => {
  const { data, loading, error } = useQueryWithContext(
    initiateExternalPaymentQuery,
    {
      variables: {
        docNumbers
      }
    }
  );

  return {
    data: data?.initiateExternalPayment,
    loading,
    error
  };
};

type Result<T> = {
  data?: T;
  loading: boolean;
  error?: Error;
} & State;
